import { customLoggingLevels, COMPONENT_TYPES } from './constants';
import { iAppConfiguration } from '../interfaces/AppConfiguration';
import configJson from './config.json';
import { parseDatadogAllowedTracingUrls } from '@intigral/utils/lib/user';
import { type DefaultPrivacyLevel, type TraceContextInjection } from '@datadog/browser-rum';

const jsonConfig: any = configJson;

const guestApiBaseUrl = (process.env.NX_PUBLIC_ENTITLEMENT_API_BASE_URL || '').trim().replace(/\/+$/, '');
const appKey = jsonConfig.appKey || process.env.NX_PUBLIC_APP_KEY || '';

export const appConfig: iAppConfiguration = {
    cmsBaseUrl: jsonConfig.cmsBaseUrl,
    appKey: jsonConfig.appKey,
    version: jsonConfig.build?.version || 0,
    appName: jsonConfig.appName,
    tokenization: {
        Api: 'https://api.intigral-ott.net/popcorn-api-rs-7.9.3/v1/auth-service/auth/refresh',
        apikey: 'stbGDMSTGExy0sVDlZMzNDdUyZ',
        serial: 'M91819ED1018',
        mac_add: '18:9c:27:54:0b:7f',
    },
    channelListApi: 'https://api.intigral-ott.net/popcorn-api-rs-7.9.4/v3/channels?apikey=stbGDMSTGExy0sVDlZMzNDdUyZ',
    defaultLanguage: jsonConfig.defaultLanguage,
    forgotPasswordUrl: jsonConfig.forgotPasswordUrl,
    userLogoutUrl: jsonConfig.logoutUrl,
    userForceLogoutUrl: jsonConfig.forceLogoutUrl,
    userProfileUrl: jsonConfig.profileUrl as string,
    userDefaultProfileName: 'default',
    vodListRowCount: 6,
    defaultApiRange: '0-12',
    deviceType: jsonConfig.deviceType,
    defaultScreen: jsonConfig.defaultScreen || 'home',
    device: jsonConfig.device,
    defaultCountryCode: jsonConfig.defaultCountryCode,
    headerCollapsePadding: 10,
    refreshTokenOffset: jsonConfig.refreshTokenOffset || 3300000,
    // refreshTokenOffset: 1760000,
    defaultPageSize: 100,
    defaultPortraitImage: 'images/placeholder/placeholder1.png',
    minDesktopSize: 768,
    jsonpCallbackParam: 'callback',
    notFoundPage: 'not-found',
    defaultSubscription: 'BASE_SUBSCRIPTION',
    loadStatusesStore: ['refreshApiInProgress', 'user_status_checked'],
    bitMovinDRMConfiguration: {
        certificateURL: window.location.origin + '/js/fps_certificate.der',
        licenseResponseType: 'json',
        useUint16InitData: true,
    },
    cookieDomain: jsonConfig.cookieDomain,
    components_images: {
        FEATURED_ITEM_DESKTOP: 1280,
        FEATURED_ITEM_TABLET: 1280,
        FEATURED_ITEM_MOBILE: 400,
        VOD_FEATURED_ITEM: 1280,
        VOD_LIST_ITEM: 400,
        VOD_ITEM: 400,
        APP_VOD_ITEM: 750,
        WATCHLIST_VOD_ITEM: 400,
        REWIND_ITEM: 500,
        WATCHLIST_REWIND_ITEM: 500,
        SEASON_EPISODES_ITEM: 500,
        WATCHLIST_SEASON_EPISODES_ITEM: 500,
        DETAILS_CONTAINER_ITEM: 750,
        CAST_CREW_ITEM: 400,
        LINEAR_ITEM: 400,
        TV_GUIDE_CAROUSEL_ITEM: 500,
        TV_GUIDE_CAROUSEL_CHANNEL_LOGO: 150,
    },
    appCustomLoggingLevel: customLoggingLevels[jsonConfig.appCustomLoggingLevel],
    displayLogsOnAppScreen: jsonConfig.displayLogsOnAppScreen,
    failedRefreshTokenAttempts: 2,
    failedRefreshTokenTimer: 1000,
    osSupport: jsonConfig.osSupport || [],
    browserSupport: {
        browsers: jsonConfig.browserSupport.browsers,
        enable: jsonConfig.browserSupport.enable,
        textMessages: jsonConfig.browserSupport.textMessages,
    },
    playerRetriedToPlayTimerOnFetchFailure: jsonConfig.playerRetriedToPlayTimerOnFetchFailureinMS,
    playerRetriedToPlayAttemptsOnFetchFailure: jsonConfig.playerRetriedToPlayAttemptsOnFetchFailure,
    autoLoginUserObject: jsonConfig.autoLoginUserObject || null,
    refreshTokenMaxAttempts: jsonConfig.refreshTokenMaxAttempts || 3,
    refreshTokenMaxTimer: jsonConfig.refreshTokenMaxTimer,
    bookmarksInterval: 5000,
    screenHistoryLimit: 10,
    reduxStoreVersion: jsonConfig.reduxStoreVersion,
    arabicDownLetters: jsonConfig.arabicDownLetters,
    configVersionAPIEndpoint: jsonConfig.configVersionAPIEndpoint,
    baseConfigs: jsonConfig.baseConfigs,
    excludedUserParams: jsonConfig.excludedUserParams || [],
    userCookieExpirationDays: jsonConfig.userCookieExpirationDays,
    productKey: process.env.NX_PUBLIC_PRODUCT_KEY || 'stc-tv',
    kochavaKey: process.env.NX_PUBLIC_MLP_KOCHAVA_KEY,
    tsLogLevel: process.env.NX_PUBLIC_TS_LOG_LEVEL,
    tsLoggerEnable: process.env.NX_PUBLIC_TS_LOGGER_ENABLE,
    screenReloadParameters: ['id', 'guid', 'q'],
    componentDataReloadOnParameters: [COMPONENT_TYPES.FULLVOD_CATALOG_LIST, COMPONENT_TYPES.REWIND_LIST],
    enableReduxLogger: (process.env.NX_PUBLIC_ENABLE_REDUX_LOGGER as unknown as boolean) || false,
    enableReactQueryDevTool: process.env.NX_PUBLIC_IS_QUERY_DEV_TOOLS_ENABLE,
    dataDogRUM: {
        isEnabled: process.env.NX_PUBLIC_DATA_DOG_RUM_IS_ENABLED?.toLowerCase() === 'true',
        appId: process.env.NX_PUBLIC_DATA_DOG_RUM_APP_ID || '',
        clientToken: process.env.NX_PUBLIC_DATA_DOG_RUM_CLIENT_TOKEN || '',
        site: process.env.NX_PUBLIC_DATA_DOG_RUM_SITE || '',
        service: process.env.NX_PUBLIC_DATA_DOG_RUM_SERVICE || '',
        env: process.env.NX_PUBLIC_DATA_DOG_RUM_ENV || '',
        version: process.env.NX_PUBLIC_DATA_DOG_RUM_APP_VERSION_NO,
        sessionSampleRate: process.env.NX_PUBLIC_DATA_DOG_RUM_SESSION_SAMPLE_RATE || 100,
        sessionReplaySampleRate: process.env.NX_PUBLIC_DATA_DOG_RUM_SESSION_REPLAY_SAMPLE_RATE || 100,
        trackUserInteractions: process.env.NX_PUBLIC_DATA_DOG_RUM_TRACK_USER_INTERACTIONS || true,
        trackResources: process.env.NX_PUBLIC_DATA_DOG_RUM_TRACK_RESOURCES || true,
        trackLongTasks: process.env.NX_PUBLIC_DATA_DOG_RUM_TRACKING_LONG_TASKS || true,
        allowedTracingUrls: parseDatadogAllowedTracingUrls(
            process.env.NX_PUBLIC_DATA_DOG_RUM_ALLOWED_TRACING_URLS,
            process.env.NX_PUBLIC_DATA_DOG_RUM_EXCLUDED_TRACING_URLS
        ),
        remoteConfigurationId: process.env.NX_PUBLIC_DATA_DOG_RUM_REMOTE_CONFIGURATION_ID,
        traceSampleRate: process.env.NX_PUBLIC_DATA_DOG_RUM_TRACE_SAMPLE_RATE || 100,
        profilingSampleRate: process.env.NX_PUBLIC_DATA_DOG_RUM_PROFILING_SAMPLE_RATE || 100,
        defaultPrivacyLevel:
            (process.env.NX_PUBLIC_DATA_DOG_RUM_DEFAULT_PRIVACY_LEVEL as DefaultPrivacyLevel) || 'mask-user-input',
        trackSessionAcrossSubdomains:
            process.env.NX_PUBLIC_DATA_DOG_RUM_TRACK_SESSION_ACROSS_SUBDOMAINS?.toLowerCase() === 'true',
        enablePrivacyForActionName:
            process.env.NX_PUBLIC_DATA_DOG_RUM_ENABLE_PRIVACY_FOR_ACTION_NAME?.toLowerCase() === 'true',
        trackAnonymousUser: process.env.NX_PUBLIC_DATA_DOG_RUM_TRACK_ANONYMOUS_USER?.toLowerCase() === 'true',
        actionNameAttribute: process.env.NX_PUBLIC_DATA_DOG_RUM_ACTION_NAME_ATTRIBUTE || '',
        traceContextInjection:
            (process.env.NX_PUBLIC_DATA_DOG_RUM_TRACE_CONTEXT_INJECTION as TraceContextInjection) || 'all',
        allowedTrackingOrigins:
            process.env.NX_PUBLIC_DATA_DOG_RUM_ALLOWED_TRACKING_ORIGINS?.split(',')
                .map((origin) => origin.trim())
                .filter(Boolean) || [],
    },
    kameleeonKey: process.env.NX_PUBLIC_KAMELEOON_KEY || '',
    moreLikeThisRowCount: 4,
    deviceGuid: '',
    cdnBalancerAccountCode: process.env.NX_PUBLIC_CDN_BALANCER_ACCOUNT_CODE,
    cdnBalancerEnabled: process.env.NX_PUBLIC_CDN_BALANCER_ENABLED === 'true' ? true : false,
    guestConfigBaseUrl:
        process.env.NX_PUBLIC_GUEST_CONFIG_URL ||
        (guestApiBaseUrl ? `${guestApiBaseUrl}/gateway/public/v2/config` : ''),
    guestLoginBaseUrl:
        process.env.NX_PUBLIC_GUEST_LOGIN_URL || (guestApiBaseUrl ? `${guestApiBaseUrl}/identity-v2/v2` : ''),
};
